import type { QrCodeSummary } from "./qr-api";
import type { OverviewFunnelKey, OverviewFunnelRow, OverviewTile } from "./stats-api";

export const OVERVIEW_WINDOWS = [7, 30, 90] as const;
export type OverviewDays = (typeof OVERVIEW_WINDOWS)[number];

export const OVERVIEW_FUNNELS: ReadonlyArray<{ key: OverviewFunnelKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "precision-main", label: "Precision - Main" },
  { key: "client-solutions", label: "Client Solutions" },
  { key: "precision-carbs", label: "Precision Carbs" },
  { key: "precision-qr", label: "Precision - QR" },
];

export const OVERVIEW_STAGES = [
  { key: "both", label: "Both" },
  { key: "pre", label: "Pre-purchase" },
  { key: "post", label: "Post-purchase" },
] as const;
export type OverviewStage = (typeof OVERVIEW_STAGES)[number]["key"];

/** The By funnel rows the Stage filter keeps: every row on Both, else only that stage's rows (merge review, PR 917). */
export function funnelRowsForStage(rows: OverviewFunnelRow[] | undefined, stage: OverviewStage): OverviewFunnelRow[] {
  const all = rows ?? [];
  return stage === "both" ? all : all.filter((row) => row.stage === stage);
}

export type OverviewUrlState = {
  days: OverviewDays;
  funnel: OverviewFunnelKey;
  stage: OverviewStage;
};

export const KPI_IDS: Record<OverviewTile["key"], string> = {
  new_app_users: "k129",
  active_users: "k482",
  stickiness: "new, beside k128 WAU/MAU",
  orders_per_app_user: "k510",
  leads: "new",
  lead_to_app: "new",
  results_given: "new (launch KPI)",
  order_made: "new (launch KPI)",
};

export const PACK_PLACEMENTS = ["label", "box", "insert"] as const;

const COUNT_KEYS = new Set<OverviewTile["key"]>(["leads", "new_app_users", "active_users"]);
const RATE_KEYS = new Set<OverviewTile["key"]>(["lead_to_app", "results_given", "order_made", "stickiness"]);
const integer = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const twoDecimals = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function overviewDates(days: OverviewDays, today: Date): { from: string; to: string } {
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(to) };
}

export function overviewUrlState(search: Record<string, unknown>): OverviewUrlState {
  const parsedDays = Number(search.ov_days);
  const days = OVERVIEW_WINDOWS.includes(parsedDays as OverviewDays) ? (parsedDays as OverviewDays) : 30;
  const funnel = OVERVIEW_FUNNELS.some((option) => option.key === search.ov_funnel)
    ? (search.ov_funnel as OverviewFunnelKey)
    : "all";
  const stage = OVERVIEW_STAGES.some((option) => option.key === search.ov_stage)
    ? (search.ov_stage as OverviewStage)
    : "both";
  return { days, funnel, stage };
}

export function overviewSearch(state: OverviewUrlState): Record<string, string> {
  return { ov_days: String(state.days), ov_funnel: state.funnel, ov_stage: state.stage };
}

export function formatOverviewValue(tile: Pick<OverviewTile, "key" | "value">): string {
  if (tile.value === null) return "Needs data";
  if (COUNT_KEYS.has(tile.key)) return integer.format(tile.value);
  if (RATE_KEYS.has(tile.key)) {
    const percentage = tile.value * 100;
    return percentage > 0 && percentage < 1 ? `${oneDecimal.format(percentage)}%` : `${integer.format(percentage)}%`;
  }
  return oneDecimal.format(tile.value);
}

function count(value: number | null | undefined): string {
  return value === null || value === undefined ? "Needs data" : integer.format(value);
}

export function overviewTileSub(tile: OverviewTile): string {
  if (tile.value === null) return tile.needs_data?.reason ?? "Needs data";
  if (tile.key === "leads") return "landing pages and Client Solutions intake";
  if (tile.key === "new_app_users") return "first open in the window";
  if (tile.key === "lead_to_app" || tile.key === "results_given" || tile.key === "order_made") {
    return `${count(tile.people)} of ${count(tile.base)} in the app`;
  }
  if (tile.key === "active_users") return "used the app in 30 days, every funnel";
  if (tile.key === "stickiness") {
    const detail = tile.detail;
    if (!detail) return "DAU/MAU";
    const wauMau = detail.wau_mau === null ? "Needs data" : formatOverviewValue({ key: "stickiness", value: detail.wau_mau });
    const covered = detail.partial ? ` (${detail.days_covered} days since launch)` : "";
    const average = detail.avg_dau === null ? "Needs data" : twoDecimals.format(detail.avg_dau);
    return `DAU/MAU: ${average} a day of ${count(detail.mau)} · WAU/MAU ${wauMau}${covered}`;
  }
  return "orders for each app user";
}

export function packQrScanners(codes: QrCodeSummary[] | undefined): number | null {
  if (codes === undefined) return null;
  return codes.reduce(
    (total, code) => total + (code.placements.some((placement) => PACK_PLACEMENTS.includes(placement as (typeof PACK_PLACEMENTS)[number])) ? code.unique_scanners : 0),
    0,
  );
}
